import {
  PolicyUpdatedActivity,
  VersionedObjectDiff,
} from '@clearcover/core-app-types';
import React, { FC, useState } from 'react';
import {
  toPolicyCoverageCardAndDetails,
  toVehicleCoverageCardAndDetails,
} from './CoverageChange';
import { toDriverCardAndDetails } from './DriverChange';
import {
  ChangeCardAndDetails,
  CoveragesAndEndorsementsDiff,
} from './PolicyChangeCard';
import { toVehicleCardAndDetails } from './VehicleChange';
import { toDiscountChangeCardAndDetails } from './DiscountChange';
import { toAddressChangeCardAndDetails } from './AddressChange';
import styles from './styles.module.css';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { toInsurableItemRelationshipChangeCardAndDetails } from './InsurableItemRelationshipChanges';
import classNames from 'classnames';

const getAllChanges = (
  activity: PolicyUpdatedActivity
): Array<ChangeCardAndDetails> => {
  const {
    insurableItems,
    insurableItemParties,
    policyCoverages,
    policyEndorsements,
    vehicleCoveragesAndEndorsements,
    multiLineDiscounts,
    addresses,
    insurableItemRelationships,
  } = activity.data.changeset;

  const versionId = activity.data.versionId;

  const policyCoveragesAndEndorsements: Array<CoveragesAndEndorsementsDiff> =
    policyEndorsements.length || policyCoverages.length
      ? [
          {
            coverages: policyCoverages,
            endorsements: policyEndorsements,
          },
        ]
      : [];

  return [
    ...insurableItems.map(item => toVehicleCardAndDetails(item, versionId)),
    ...insurableItemParties.map(toDriverCardAndDetails),
    ...policyCoveragesAndEndorsements.map(coverage =>
      toPolicyCoverageCardAndDetails(coverage, versionId)
    ),
    ...vehicleCoveragesAndEndorsements.map(coverage =>
      toVehicleCoverageCardAndDetails(coverage, versionId)
    ),
    ...multiLineDiscounts.map((item: VersionedObjectDiff<any>) =>
      toDiscountChangeCardAndDetails(item)
    ),
    ...addresses.map((item: VersionedObjectDiff<any>) =>
      toAddressChangeCardAndDetails(item)
    ),
    ...insurableItemRelationships.map((item: VersionedObjectDiff<any>) =>
      toInsurableItemRelationshipChangeCardAndDetails(item)
    ),
  ];
};

export const PolicyChanges: FC<{
  activity: PolicyUpdatedActivity;
  className?: String;
}> = ({ activity, className }) => {
  const allChanges = getAllChanges(activity);

  const [selectedIndex, setSelected] = useState(0);
  if (allChanges.length === 0) {
    return <></>;
  }

  const SelectedDetails = allChanges[selectedIndex].details ?? (() => <></>);

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.firstColumn}>
        {allChanges.map(({ card: SummaryCard }, index) => (
          <SummaryCard
            key={index}
            data-testid={`policy-change-${index}`}
            selected={index === selectedIndex}
            onClick={() => setSelected(index)}
          />
        ))}
      </div>
      <div className={styles.secondColumn}>
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={selectedIndex}
            timeout={100}
            unmountOnExit
            mountOnEnter
            classNames={styles}
          >
            <SelectedDetails />
          </CSSTransition>
        </SwitchTransition>
      </div>
    </div>
  );
};
